import {DEFAULT_LOCALE} from "i18n/localePreference";
import {brandDomainFor} from "nav/siteLinkOrigin";
import {siteOrigin} from "seo/siteMeta";

/**
 * The support inbox to name to the visitor, derived from the storefront they are actually on
 * (kitecorp/transferify-ui#2261). React-free by mandate (epic #26).
 *
 * The api routes support mail to the BRAND's own group — `support@transferify.cloud` for
 * transferify.cloud, `support@transferify.ro` for transferify.ro (api#1481) — and names that address
 * in its delivery-failed error (api#1487). Before this module the ui published one hardcoded `.ro`
 * address from its catalogs, so every visitor of transferify.cloud was told to write to an inbox
 * their own request would not have been routed to.
 *
 * The storefront comes from {@link brandDomainFor}, the host→brand rule `nav/siteLinkOrigin` already
 * owns, rather than a second `transferify\.(cloud|ro)` match here: two rules answering "which brand
 * am I on" is how they end up disagreeing, and that one already knows about `www.`, about `dev.` and
 * about a customer's claimed subdomain belonging to the brand it sits under (devops#142).
 */

/**
 * The local part, identical for every brand — both are Google Groups named `support@<brand>`.
 *
 * A brand whose inbox is not `support@` would need a per-brand table here, the same way api#1481 put
 * the address on the brand's configuration rather than deriving it from the brand key. Today there
 * is no such brand, and a two-entry table restating the same word twice would be the harder thing to
 * keep true.
 */
const SUPPORT_MAILBOX = "support";

/**
 * The brand to answer for a host that carries no brand signal.
 *
 * Three real cases reach it: the pre-render and the first client render (which see `""` by design —
 * see `utils/useMountedHostname`), a development machine, and a Premium BYO custom domain. The answer
 * is the DEFAULT locale's brand, which is what `siteLinkOrigin` already hands a BYO custom domain and
 * what the api falls back to as well (`brands.fallback-key: transferify.cloud`), so the inbox named
 * is the one the api would have routed that same request to. A ui default of `.ro` here would
 * contradict the delivery it is describing.
 *
 * Derived from the topology rather than written out: `deployed-topology-sweep` refuses a second copy
 * of a deployed host, and it is right to — a literal here would keep naming the old brand after a
 * domain change, and the failure would be a support address nobody reads rather than an error.
 */
const FALLBACK_BRAND_DOMAIN = new URL(siteOrigin(DEFAULT_LOCALE)).hostname;

/**
 * The support address for the host the page is being served from.
 *
 * @param hostname `window.location.hostname`, or `""` before mount. Callers in React take it from
 *   {@link ../utils/useMountedHostname.useMountedHostname} through {@link useSupportAddress}.
 */
export function supportAddressFor(hostname: string): string {
	return `${SUPPORT_MAILBOX}@${brandDomainFor(hostname) ?? FALLBACK_BRAND_DOMAIN}`;
}
